import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'vipUserRole',
    'taskListDashboard',
    'receiveDailyDigest',
    'rpmRecordingType',
    'installer',
    'defaultDashboard',
  ];

  static values = {
    formType: String,
  };

  connect() {
    // Initialize form state based on form type
    this.initializeFormState();
  }

  initializeFormState() {
    const selectedRoleRadio = this.element.querySelector(
      'input[type="radio"][name*="organisation_role_id"]:checked'
    );

    if (
      !selectedRoleRadio ||
      !selectedRoleRadio.dataset.roleSettingsConfigValue
    ) {
      return;
    }

    if (this.formTypeValue === 'creation') {
      // A radio pre-checked by the server (single assignable role) never
      // fires `change`, so replay it to run every action bound on the radio
      // (radio-toggled-checkbox#toggle and updateSettings) exactly as if the
      // user had selected the role. Relies on radio-toggled-checkbox being
      // listed before role-settings-updater in the form's data-controller so
      // its listeners are already bound when we connect.
      selectedRoleRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // Edit forms: only update enabled/disabled states, preserve saved
      // values
      this.updateSettingsVisibilityOnly({ currentTarget: selectedRoleRadio });
    }
  }

  updateSettings(event) {
    const selectedRoleRadio = event.currentTarget;
    if (!selectedRoleRadio) {
      return;
    }
    if (!selectedRoleRadio.dataset.roleSettingsConfigValue) {
      return;
    }

    let config;
    try {
      config = JSON.parse(selectedRoleRadio.dataset.roleSettingsConfigValue);
    } catch (e) {
      return;
    }

    // Update standard settings
    this.updateStandardSettings(config);

    // Update VIP tabs and features checkboxes
    this.updateVipTabsAndFeatures(selectedRoleRadio);
  }

  updateSettingsOnly(event) {
    const selectedRoleRadio = event.currentTarget;
    if (!selectedRoleRadio) {
      return;
    }
    if (!selectedRoleRadio.dataset.roleSettingsConfigValue) {
      return;
    }

    let config;
    try {
      config = JSON.parse(selectedRoleRadio.dataset.roleSettingsConfigValue);
    } catch (e) {
      return;
    }

    // Only update standard settings, not VIP tabs and features
    this.updateStandardSettings(config);
  }

  updateSettingsVisibilityOnly(event) {
    const selectedRoleRadio = event.currentTarget;
    if (!selectedRoleRadio) {
      return;
    }
    if (!selectedRoleRadio.dataset.roleSettingsConfigValue) {
      return;
    }

    let config;
    try {
      config = JSON.parse(selectedRoleRadio.dataset.roleSettingsConfigValue);
    } catch (e) {
      return;
    }

    // Only update visibility/enabled state, preserve existing values
    this.updateStandardSettingsVisibilityOnly(config);

    // Update VIP tabs and features visibility, but preserve existing checked values
    this.updateVipTabsAndFeaturesVisibilityOnly(selectedRoleRadio);
  }

  updateStandardSettings(config) {
    // Update VIP User Role (Radio Group Wrapper)
    if (this.hasVipUserRoleTarget) {
      this.updateRadioGroupVisibility(
        this.vipUserRoleTarget,
        config.vipUserRole
      );
      this.setRadioGroupDefaultValue(
        this.vipUserRoleTarget,
        config.vipUserRole
      );
    }

    // Update Task List Dashboard (Checkbox Wrapper)
    if (this.hasTaskListDashboardTarget) {
      this.updateStandardFieldVisibility(
        this.taskListDashboardTarget,
        config.taskListDashboard
      );
      this.setStandardFieldDefaultValue(
        this.taskListDashboardTarget,
        config.taskListDashboard
      );
    }

    // Update Receive Daily Digest (Checkbox Wrapper)
    if (this.hasReceiveDailyDigestTarget) {
      this.updateStandardFieldVisibility(
        this.receiveDailyDigestTarget,
        config.receiveDailyDigest
      );
      this.setStandardFieldDefaultValue(
        this.receiveDailyDigestTarget,
        config.receiveDailyDigest
      );
    }

    // Update RPM Recording Type (Select Wrapper)
    if (this.hasRpmRecordingTypeTarget) {
      this.updateStandardFieldVisibility(
        this.rpmRecordingTypeTarget,
        config.rpmRecordingType
      );
      this.setStandardFieldDefaultValue(
        this.rpmRecordingTypeTarget,
        config.rpmRecordingType
      );
    }

    // Update Installer (Checkbox Wrapper)
    if (this.hasInstallerTarget) {
      this.updateStandardFieldVisibility(
        this.installerTarget,
        config.installer
      );
      this.setStandardFieldDefaultValue(this.installerTarget, config.installer);
    }

    // Update Default Dashboard (Select Wrapper)
    if (this.hasDefaultDashboardTarget) {
      this.updateStandardFieldVisibility(
        this.defaultDashboardTarget,
        config.defaultDashboard
      );
      this.setStandardFieldDefaultValue(
        this.defaultDashboardTarget,
        config.defaultDashboard
      );
    }
  }

  updateStandardSettingsVisibilityOnly(config) {
    // Update VIP User Role (Radio Group Wrapper) - visibility only
    if (this.hasVipUserRoleTarget) {
      this.updateRadioGroupVisibilityOnly(
        this.vipUserRoleTarget,
        config.vipUserRole
      );
    }

    // Update Task List Dashboard (Checkbox Wrapper) - visibility only
    if (this.hasTaskListDashboardTarget) {
      this.updateStandardFieldVisibilityOnly(
        this.taskListDashboardTarget,
        config.taskListDashboard
      );
    }

    // Update Receive Daily Digest (Checkbox Wrapper) - visibility only
    if (this.hasReceiveDailyDigestTarget) {
      this.updateStandardFieldVisibilityOnly(
        this.receiveDailyDigestTarget,
        config.receiveDailyDigest
      );
    }

    // Update RPM Recording Type (Select Wrapper) - visibility only
    if (this.hasRpmRecordingTypeTarget) {
      this.updateStandardFieldVisibilityOnly(
        this.rpmRecordingTypeTarget,
        config.rpmRecordingType
      );
    }

    // Update Installer (Checkbox Wrapper) - visibility only
    if (this.hasInstallerTarget) {
      this.updateStandardFieldVisibilityOnly(
        this.installerTarget,
        config.installer
      );
    }

    // Update Default Dashboard (Select Wrapper) - visibility only
    if (this.hasDefaultDashboardTarget) {
      this.updateStandardFieldVisibilityOnly(
        this.defaultDashboardTarget,
        config.defaultDashboard
      );
    }
  }

  updateVipTabsAndFeatures(selectedRoleRadio) {
    // Get the selected role ID
    const selectedRoleId = selectedRoleRadio.value;

    // Find all checkboxes with radio-toggled-checkbox data attributes
    const checkboxes = this.element.querySelectorAll(
      'input[type="checkbox"][data-radio-toggled-checkbox-condition]'
    );

    checkboxes.forEach((checkbox) => {
      const conditionData = checkbox.dataset.radioToggledCheckboxCondition;
      const checkedConditionData =
        checkbox.dataset.radioToggledCheckboxCheckedCondition;

      let enableCondition, checkedCondition;
      try {
        enableCondition = JSON.parse(conditionData);
        checkedCondition = JSON.parse(checkedConditionData);
      } catch (e) {
        return;
      }

      // Check if this checkbox should be enabled for the selected role
      const shouldBeEnabled = enableCondition.value
        .split(',')
        .includes(selectedRoleId);

      // Check if this checkbox should be checked by default for the selected role
      const shouldBeChecked = checkedCondition.value
        .split(',')
        .includes(selectedRoleId);

      // Update checkbox state
      checkbox.disabled = !shouldBeEnabled;
      checkbox.checked = shouldBeChecked;

      // Handle hidden fields for disabled but checked checkboxes
      if (!shouldBeEnabled && shouldBeChecked) {
        this.addHiddenFieldForDisabledCheckbox(checkbox);
      } else {
        this.removeHiddenFieldForCheckbox(checkbox);
      }
    });
  }

  updateVipTabsAndFeaturesVisibilityOnly(selectedRoleRadio) {
    // Get the selected role ID
    const selectedRoleId = selectedRoleRadio.value;

    // Find all checkboxes with radio-toggled-checkbox data attributes
    const checkboxes = this.element.querySelectorAll(
      'input[type="checkbox"][data-radio-toggled-checkbox-condition]'
    );

    checkboxes.forEach((checkbox) => {
      const conditionData = checkbox.dataset.radioToggledCheckboxCondition;

      let enableCondition;
      try {
        enableCondition = JSON.parse(conditionData);
      } catch (e) {
        return;
      }

      // Check if this checkbox should be enabled for the selected role
      const shouldBeEnabled = enableCondition.value
        .split(',')
        .includes(selectedRoleId);

      // Update only the disabled state, preserve existing checked state
      checkbox.disabled = !shouldBeEnabled;
      // Don't update checkbox.checked to preserve existing values

      // Handle hidden fields for disabled checkboxes
      if (!shouldBeEnabled && checkbox.checked) {
        this.addHiddenFieldForDisabledCheckbox(checkbox);
      } else {
        this.removeHiddenFieldForCheckbox(checkbox);
      }
    });
  }

  updateStandardFieldVisibility(wrapperTarget, settingConfig) {
    const inputElement = wrapperTarget.querySelector(
      'input[type="checkbox"], select'
    );
    if (!inputElement) {
      return;
    }
    if (!settingConfig.optional) {
      // Keep visible but disabled, don't hide
      wrapperTarget.classList.remove('d-none');
      inputElement.disabled = true;
      // Add hidden field to ensure value is submitted
      this.addHiddenFieldForDisabledInput(
        wrapperTarget,
        inputElement,
        settingConfig
      );
    } else {
      wrapperTarget.classList.remove('d-none');
      inputElement.disabled = false;
      // Remove hidden field since input is enabled
      this.removeHiddenFieldForInput(wrapperTarget, inputElement);
    }
  }

  updateStandardFieldVisibilityOnly(wrapperTarget, settingConfig) {
    const inputElement = wrapperTarget.querySelector(
      'input[type="checkbox"], select'
    );
    if (!inputElement) {
      return;
    }

    if (!settingConfig.optional) {
      // Keep visible but disabled, preserve current value
      wrapperTarget.classList.remove('d-none');
      inputElement.disabled = true;
      // Add hidden field with current value to ensure it's submitted
      this.addHiddenFieldForDisabledInputWithCurrentValue(
        wrapperTarget,
        inputElement
      );
    } else {
      wrapperTarget.classList.remove('d-none');
      inputElement.disabled = false;
      // Remove hidden field since input is enabled
      this.removeHiddenFieldForInput(wrapperTarget, inputElement);
    }
  }

  setStandardFieldDefaultValue(wrapperTarget, settingConfig) {
    const inputElement = wrapperTarget.querySelector(
      'input[type="checkbox"], select'
    );
    if (!inputElement) {
      return;
    }

    if (inputElement.type === 'checkbox') {
      inputElement.checked = settingConfig.default;
    } else {
      // Assumes select or other text-based input types
      inputElement.value = settingConfig.default;
    }
  }

  updateRadioGroupVisibility(wrapperTarget, settingConfig) {
    const inputElements = Array.from(
      wrapperTarget.querySelectorAll('input[type="radio"]')
    );
    if (inputElements.length === 0) return;

    if (!settingConfig.optional) {
      // Keep visible but disabled, don't hide
      wrapperTarget.classList.remove('d-none');
      inputElements.forEach((inputEl) => (inputEl.disabled = true));
      // Add hidden field to ensure value is submitted
      this.addHiddenFieldForDisabledRadioGroup(
        wrapperTarget,
        inputElements,
        settingConfig
      );
    } else {
      wrapperTarget.classList.remove('d-none');
      inputElements.forEach((inputEl) => (inputEl.disabled = false));
      // Remove hidden field since radio group is enabled
      this.removeHiddenFieldForRadioGroup(wrapperTarget, inputElements);
    }
  }

  updateRadioGroupVisibilityOnly(wrapperTarget, settingConfig) {
    const inputElements = Array.from(
      wrapperTarget.querySelectorAll('input[type="radio"]')
    );
    if (inputElements.length === 0) return;

    if (!settingConfig.optional) {
      // Keep visible but disabled, preserve current value
      wrapperTarget.classList.remove('d-none');
      inputElements.forEach((inputEl) => (inputEl.disabled = true));
      // Add hidden field with current value to ensure it's submitted
      this.addHiddenFieldForDisabledRadioGroupWithCurrentValue(
        wrapperTarget,
        inputElements
      );
    } else {
      wrapperTarget.classList.remove('d-none');
      inputElements.forEach((inputEl) => (inputEl.disabled = false));
      // Remove hidden field since radio group is enabled
      this.removeHiddenFieldForRadioGroup(wrapperTarget, inputElements);
    }
  }

  setRadioGroupDefaultValue(wrapperTarget, settingConfig) {
    if (!wrapperTarget || !settingConfig) {
      return;
    }

    const inputElements = Array.from(
      wrapperTarget.querySelectorAll('input[type="radio"]')
    );
    if (inputElements.length === 0) {
      return;
    }

    inputElements.forEach((inputEl) => {
      if (inputEl.value === settingConfig.default) {
        inputEl.checked = true;
      } else {
        inputEl.checked = false;
      }
    });
  }

  addHiddenFieldForDisabledInput(wrapperTarget, inputElement, settingConfig) {
    // First remove any existing hidden field
    this.removeHiddenFieldForInput(wrapperTarget, inputElement);

    // Create hidden field with same name as the disabled input
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = inputElement.name;
    hiddenInput.value = settingConfig.default;
    hiddenInput.classList.add('stimulus-hidden-field');

    // Add hidden field to wrapper
    wrapperTarget.appendChild(hiddenInput);
  }

  addHiddenFieldForDisabledInputWithCurrentValue(wrapperTarget, inputElement) {
    // First remove any existing hidden field
    this.removeHiddenFieldForInput(wrapperTarget, inputElement);

    // Create hidden field with same name as the disabled input
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = inputElement.name;
    // Use current field value instead of config default
    hiddenInput.value =
      inputElement.type === 'checkbox'
        ? inputElement.checked
        : inputElement.value;
    hiddenInput.classList.add('stimulus-hidden-field');

    // Add hidden field to wrapper
    wrapperTarget.appendChild(hiddenInput);
  }

  removeHiddenFieldForInput(wrapperTarget, inputElement) {
    // Remove any existing hidden field with same name
    const existingHiddenField = wrapperTarget.querySelector(
      `input[type="hidden"][name="${inputElement.name}"].stimulus-hidden-field`
    );
    if (existingHiddenField) {
      existingHiddenField.remove();
    }
  }

  addHiddenFieldForDisabledRadioGroup(
    wrapperTarget,
    inputElements,
    settingConfig
  ) {
    // First remove any existing hidden field
    this.removeHiddenFieldForRadioGroup(wrapperTarget, inputElements);

    // Get the name from the first radio button (they all have the same name)
    const fieldName = inputElements[0].name;

    // Create hidden field with same name as the disabled radio group
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = fieldName;
    hiddenInput.value = settingConfig.default;
    hiddenInput.classList.add('stimulus-hidden-field');

    // Add hidden field to wrapper
    wrapperTarget.appendChild(hiddenInput);
  }

  addHiddenFieldForDisabledRadioGroupWithCurrentValue(
    wrapperTarget,
    inputElements
  ) {
    // First remove any existing hidden field
    this.removeHiddenFieldForRadioGroup(wrapperTarget, inputElements);

    // Find the currently selected radio button
    const selectedRadio = inputElements.find((input) => input.checked);
    const currentValue = selectedRadio ? selectedRadio.value : '';

    // Get the name from the first radio button (they all have the same name)
    const fieldName = inputElements[0].name;

    // Create hidden field with same name as the disabled radio group
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = fieldName;
    // Use current field value instead of config default
    hiddenInput.value = currentValue;
    hiddenInput.classList.add('stimulus-hidden-field');

    // Add hidden field to wrapper
    wrapperTarget.appendChild(hiddenInput);
  }

  removeHiddenFieldForRadioGroup(wrapperTarget, inputElements) {
    // Get the name from the first radio button (they all have the same name)
    const fieldName = inputElements[0].name;

    // Remove any existing hidden field with same name
    const existingHiddenField = wrapperTarget.querySelector(
      `input[type="hidden"][name="${fieldName}"].stimulus-hidden-field`
    );
    if (existingHiddenField) {
      existingHiddenField.remove();
    }
  }

  addHiddenFieldForDisabledCheckbox(checkbox) {
    // First remove any existing hidden field
    this.removeHiddenFieldForCheckbox(checkbox);

    // Create hidden field with same name and value as the disabled checkbox
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = checkbox.name;
    hiddenInput.value = checkbox.value;
    hiddenInput.classList.add('stimulus-hidden-field');

    // Add hidden field after the checkbox
    checkbox.parentNode.insertBefore(hiddenInput, checkbox.nextSibling);
  }

  removeHiddenFieldForCheckbox(checkbox) {
    // Remove any existing hidden field with same name and value
    const existingHiddenField = checkbox.parentNode.querySelector(
      `input[type="hidden"][name="${checkbox.name}"][value="${checkbox.value}"].stimulus-hidden-field`
    );
    if (existingHiddenField) {
      existingHiddenField.remove();
    }
  }
}
